import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import { TRAINING_ATTENDANCE, TRAINING_STATUS_UPDATE } from "../../routes/routes";
import { NoDataFound, Pagination } from "../../components/Utils";
import Loader from "../../components/common/Loader";
import StickyTable from "../../components/common/StickyTable";

function statusBadge(data) {
  if (data?.timing === "past") return { text: "Past", background: "#f3f4f6", color: "#6b7280" };
  if (data?.timing === "in_progress") return { text: "In progress", background: "#dcfce7", color: "#15803d" };
  return { text: "Upcoming", background: "#fef3c7", color: "#a16207" };
}

function placeLine(data) {
  const left = [data?.city_name, data?.state_name].filter(Boolean).join(", ");
  if (left && data?.region_name) return `${left} · ${data.region_name}`;
  return left || data?.region_name || "";
}

const TrainingAttendance = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const load = () => {
    setLoading(true);
    axios.get(TRAINING_ATTENDANCE + id)
      .then((response) => setData(response.data.data))
      .catch((err) => toast.error(err.response?.data?.msg || "Unable to load training"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const members = useMemo(() => {
    const list = Array.isArray(data?.members) ? data.members : [];
    return list.filter((row) => String(row.user_name || "").toLowerCase().includes(search.trim().toLowerCase()));
  }, [data, search]);

  const visible = members.slice((page - 1) * perPage, page * perPage);
  const paginator = {
    current: page,
    last: Math.max(Math.ceil(members.length / perPage), 1),
    total: members.length,
  };
  const badge = statusBadge(data);

  const updateStatus = (scheduleId, status) => {
    axios.post(TRAINING_STATUS_UPDATE + scheduleId, { status })
      .then((response) => {
        toast.success(response.data.msg || "Training attendance updated successfully");
        load();
      })
      .catch((err) => toast.error(err.response?.data?.msg || "Unable to update attendance"));
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Training Attendance</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to="/dashboard">Dashboard</Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <Link to="/trainings">Training</Link>
                </li>
                <li>
                  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Attendance</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <Link to="/trainings">
                  <button type="button" className="btn btn-dark igoback_btn">
                    <i className="bx bx-exit"></i> back
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="container-fluid p-0">
          {data && (
            <div className="card mb-3" style={{ borderRadius: 16 }}>
              <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center" style={{ gap: 14, minWidth: 220 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: "#fde8ec", color: "#e11d48", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                    <i className="bx bx-calendar"></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: "#111827", lineHeight: 1.2 }}>{data.chapter_name}</div>
                    <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>{placeLine(data)}</div>
                  </div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: 12 }}>Training Date</div>
                  <div style={{ fontWeight: 700, color: "#111827" }}>{data.training_date || "-"}</div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: 12 }}>Time</div>
                  <div style={{ fontWeight: 700, color: "#111827" }}>{data.training_time || "-"}</div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 4 }}>Status</div>
                  <span style={{ background: badge.background, color: badge.color, borderRadius: 999, padding: "4px 12px", fontSize: 13, fontWeight: 600 }}>{badge.text}</span>
                </div>
                {[
                  { label: "Attended", value: data.attended_count, background: "#e7f8ee", color: "#15803d" },
                  { label: "Absent", value: data.absent_count, background: "#fde8e8", color: "#dc2626" },
                  { label: "Not marked", value: data.unmarked_count, background: "#fff4e5", color: "#d97706" },
                ].map((item) => (
                  <div key={item.label} style={{ background: item.background, borderRadius: 14, padding: "10px 18px", minWidth: 108 }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: item.color, lineHeight: 1.1 }}>{item.value ?? 0}</div>
                    <div style={{ fontSize: 13, color: item.color, marginTop: 2 }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card icard sticky-table-card">
            <div className="container-fluid f_containter">
              <div className="row mb-4">
                <div className="col-md-3 mb-3">
                  <label className="form-label" htmlFor="member_search">Member</label>
                  <input
                    id="member_search"
                    className="form-control"
                    placeholder="Search member"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              {data && !data.started && (
                <div
                  className="mb-4 d-flex align-items-center"
                  style={{
                    backgroundColor: "#fffbeb",
                    border: "1px solid #fde68a",
                    color: "#92400e",
                    borderRadius: "12px",
                    padding: "12px 18px",
                    gap: "12px",
                    fontSize: "14px",
                    fontWeight: 500,
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#fef3c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#d97706",
                      fontSize: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <i className="bx bx-time-five"></i>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600 }}>Notice: </span>
                    Attendance can be marked after the training starts.
                  </div>
                </div>
              )}
            </div>
            <StickyTable refreshDeps={[visible, loading]}>
              <table className="table table-striped itable">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>Member</th>
                    <th style={{ textAlign: "center" }}>Attended</th>
                    <th style={{ textAlign: "center" }}>Absent</th>
                  </tr>
                </thead>
                <tbody className="table-border-bottom-0">
                  {loading ? (
                    <Loader loading={loading} colspan="4" />
                  ) : visible.length > 0 ? (
                    visible.map((row, index) => (
                      <tr key={row.id}>
                        <td>{(page - 1) * perPage + index + 1}</td>
                        <td>
                          <span className="table_img pr-2"><img src={row.profile_image} alt="icon" /></span>
                          {row.user_name}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Form.Check
                            type="switch"
                            id={`att-${row.id}`}
                            className="m-0"
                            checked={row.training_status === 1}
                            disabled={!data?.started}
                            label=""
                            title={data?.started ? "Attended" : "Attendance can be marked after the training starts"}
                            onChange={(event) => updateStatus(row.id, event.target.checked ? 1 : 0)}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Form.Check
                            type="switch"
                            id={`abs-${row.id}`}
                            className="m-0"
                            checked={row.training_status === 2}
                            disabled={!data?.started}
                            label=""
                            title={data?.started ? "Absent" : "Attendance can be marked after the training starts"}
                            onChange={(event) => updateStatus(row.id, event.target.checked ? 2 : 0)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoDataFound colspan="4" message="No Data Found" />
                  )}
                </tbody>
              </table>
            </StickyTable>
            {!loading && members.length > 0 && (
              <Pagination paginator={paginator} page={(no) => setPage(no)} perPage={perPage} setPerPage={setPerPage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingAttendance;
